/**
 * Okamžité nápovědy k heslu v prohlížeči (bez sítě). Jen orientační — rozhoduje
 * serverová kontrola `passwords.check` / brána v Lidech (stejná pravidla:
 * minimální délka podle politiky adresáře, login/jméno/mail, řady a opakování).
 */

/** Náhradní minimum, dokud server neřekl politiku adresáře (stejné jako na serveru). */
export const PASSWORD_FALLBACK_MIN_LENGTH = 8;

export type PasswordHintCode =
  | 'length'
  | 'complexity'
  | 'contains_login'
  | 'contains_name'
  | 'contains_email'
  | 'sequence'
  | 'repetition';

export interface PasswordHintContext {
  login?: string;
  names?: string[];
  emails?: string[];
  minLength?: number;
  /** Tři ze čtyř skupin znaků (AD složitost). */
  requireComplexity?: boolean;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ROWS = [
  '1234567890',
  'qwertyuiop',
  'qwertzuiop',
  'asdfghjkl',
  'yxcvbnm',
  'zxcvbnm',
  'abcdefghijklmnopqrstuvwxyz',
];

export function normalizeHint(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function tokens(value: string): string[] {
  const n = normalizeHint(value.trim());
  if (!n || UUID_RE.test(n)) return [];
  return n.split(/[\s,.\-_#@+\\/]+/).filter((part) => part.length >= 3);
}

function contains(password: string, list: string[]): boolean {
  const n = normalizeHint(password);
  return list.some((token) => n.includes(token));
}

export function hasRun(password: string): { sequence: boolean; repetition: boolean } {
  const n = normalizeHint(password);
  const sequence = ROWS.some((row) => {
    const reversed = [...row].reverse().join('');
    for (let i = 0; i + 4 <= row.length; i++) {
      if (n.includes(row.slice(i, i + 4)) || n.includes(reversed.slice(i, i + 4))) return true;
    }
    return false;
  });
  return { sequence, repetition: /(.)\1{3,}/u.test(n) };
}

export function passwordHints(
  password: string,
  context: PasswordHintContext = {},
): PasswordHintCode[] {
  const out: PasswordHintCode[] = [];
  if ([...password].length < (context.minLength ?? PASSWORD_FALLBACK_MIN_LENGTH)) {
    out.push('length');
  }
  if (context.requireComplexity) {
    const groups = [/[A-Z]/, /[a-z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((re) =>
      re.test(password)
    ).length;
    if (groups < 3) out.push('complexity');
  }
  if (contains(password, tokens(context.login ?? ''))) out.push('contains_login');
  if (contains(password, (context.names ?? []).flatMap(tokens))) out.push('contains_name');
  if (
    contains(
      password,
      (context.emails ?? []).flatMap((email) => tokens(email.split('@')[0] ?? '')),
    )
  ) {
    out.push('contains_email');
  }
  const run = hasRun(password);
  if (run.sequence) out.push('sequence');
  if (run.repetition) out.push('repetition');
  return out;
}
